import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Grid,
  TextField,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Campaign,
  Add,
  Delete,
  CheckCircle,
  Schedule,
  PlayArrow,
  Pause,
  Cancel,
  Edit,
  CalendarToday,
  Person,
  Assignment
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axiosinstance from '../../utils/axiosConfig';

interface MarketingScheduleProps {
  resource: any;
  onUpdate: (updatedResource: any) => void;
}

interface MarketingScheduleItem {
  _id?: string;
  action: string;
  platform: string;
  scheduledDate: Date;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled' | 'deferred' | 'skipped';
  assignedTo?: string;
  notes?: string;
  priority: 'low' | 'medium' | 'high';
}

const MarketingSchedule: React.FC<MarketingScheduleProps> = ({ resource, onUpdate }) => {
  const [marketingSchedule, setMarketingSchedule] = useState<MarketingScheduleItem[]>(
    resource?.marketingSchedule || []
  );
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [newScheduleItem, setNewScheduleItem] = useState<MarketingScheduleItem>({
    action: '',
    platform: '',
    scheduledDate: new Date(),
    status: 'planned',
    notes: '',
    priority: 'medium'
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const marketingPlatforms = [
    'Instagram', 'Facebook', 'Twitter', 'TikTok', 'LinkedIn', 'YouTube',
    'Email Newsletter', 'Website', 'Press', 'Radio', 'Print', 'Other'
  ];

  const actionTypes = [
    'Press Release', 'Social Media Post', 'Instagram Story', 'Facebook Event',
    'Email Campaign', 'Website Update', 'Radio Interview', 'Photo Shoot',
    'Video Content', 'Blog Post', 'Ticket Launch', 'Review Request',
    'Media Kit Distribution', 'Poster Distribution', 'Influencer Outreach'
  ];

  const teamMembers = [
    'Marketing Team', 'Social Media Manager', 'PR Manager', 'Design Team', 
    'Content Creator', 'External Agency'
  ];

  const getStatusIcon = (status: MarketingScheduleItem['status']) => {
    switch (status) {
      case 'completed': return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'in_progress': return <PlayArrow sx={{ color: 'warning.main' }} />;
      case 'cancelled': return <Cancel sx={{ color: 'error.main' }} />;
      case 'deferred': return <Pause sx={{ color: 'info.main' }} />;
      case 'skipped': return <Cancel sx={{ color: 'grey.500' }} />;
      default: return <Schedule sx={{ color: 'grey.400' }} />;
    }
  };

  const getStatusColor = (status: MarketingScheduleItem['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'cancelled': return 'error';
      case 'deferred': return 'info';
      case 'skipped': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: MarketingScheduleItem['priority']) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const addOrUpdateScheduleItem = async () => {
    const item = {
      ...newScheduleItem,
      scheduledDate: newScheduleItem.scheduledDate instanceof Date 
        ? newScheduleItem.scheduledDate 
        : new Date(newScheduleItem.scheduledDate)
    };

    let updatedSchedule;
    if (editingIndex !== null) {
      // Update existing item
      updatedSchedule = [...marketingSchedule];
      updatedSchedule[editingIndex] = item;
    } else {
      // Add new item
      updatedSchedule = [...marketingSchedule, item];
    }

    setMarketingSchedule(updatedSchedule);

    // Save to backend
    try {
      const cleanedMarketingSchedule = updatedSchedule.map(scheduleItem => {
        const { _id, ...cleanItem } = scheduleItem;
        return {
          ...cleanItem,
          scheduledDate: scheduleItem.scheduledDate instanceof Date 
            ? scheduleItem.scheduledDate.toISOString() 
            : new Date(scheduleItem.scheduledDate).toISOString()
        };
      });

      await axiosinstance.put(`/api/resources/${resource._id}`, {
        marketingSchedule: cleanedMarketingSchedule
      });

      onUpdate({
        ...resource,
        marketingSchedule: updatedSchedule
      });
    } catch (error) {
      console.error('Error saving marketing schedule:', error);
    }

    resetForm();
  };

  const resetForm = () => {
    setScheduleDialogOpen(false);
    setEditingIndex(null);
    setNewScheduleItem({
      action: '',
      platform: '',
      scheduledDate: new Date(),
      status: 'planned',
      notes: '',
      priority: 'medium'
    });
  };

  const updateScheduleItemStatus = async (index: number, status: MarketingScheduleItem['status']) => {
    const updatedSchedule = marketingSchedule.map((item, i) => 
      i === index ? { ...item, status } : item
    );
    setMarketingSchedule(updatedSchedule);
    
    // Save to backend
    try {
      const cleanedMarketingSchedule = updatedSchedule.map(item => {
        const { _id, ...cleanItem } = item;
        return {
          ...cleanItem,
          scheduledDate: item.scheduledDate instanceof Date 
            ? item.scheduledDate.toISOString() 
            : new Date(item.scheduledDate).toISOString()
        };
      });

      await axiosinstance.put(`/api/resources/${resource._id}`, {
        marketingSchedule: cleanedMarketingSchedule
      });

      onUpdate({
        ...resource,
        marketingSchedule: updatedSchedule
      });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const removeScheduleItem = async (index: number) => {
    const updatedSchedule = marketingSchedule.filter((_, i) => i !== index);
    setMarketingSchedule(updatedSchedule);
    
    // Save to backend
    try {
      const cleanedMarketingSchedule = updatedSchedule.map(item => {
        const { _id, ...cleanItem } = item;
        return {
          ...cleanItem,
          scheduledDate: item.scheduledDate instanceof Date 
            ? item.scheduledDate.toISOString() 
            : new Date(item.scheduledDate).toISOString()
        };
      });

      await axiosinstance.put(`/api/resources/${resource._id}`, {
        marketingSchedule: cleanedMarketingSchedule
      });

      onUpdate({
        ...resource,
        marketingSchedule: updatedSchedule
      });
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const editScheduleItem = (index: number) => {
    const item = marketingSchedule[index];
    setNewScheduleItem({
      ...item,
      scheduledDate: new Date(item.scheduledDate)
    });
    setEditingIndex(index);
    setScheduleDialogOpen(true);
  };

  const getFilteredSchedule = () => {
    switch (activeTab) {
      case 1: return marketingSchedule.filter(item => item.status === 'in_progress');
      case 2: return marketingSchedule.filter(item => item.status === 'completed');
      case 3: return marketingSchedule.filter(item => ['cancelled', 'deferred', 'skipped'].includes(item.status));
      default: return marketingSchedule;
    }
  };

  const getTabCount = (status: string) => {
    switch (status) {
      case 'in_progress': return marketingSchedule.filter(item => item.status === 'in_progress').length;
      case 'completed': return marketingSchedule.filter(item => item.status === 'completed').length;
      case 'other': return marketingSchedule.filter(item => ['cancelled', 'deferred', 'skipped'].includes(item.status)).length;
      default: return marketingSchedule.length;
    }
  };

  const getProgressStats = () => {
    const total = marketingSchedule.length;
    const completed = marketingSchedule.filter(item => item.status === 'completed').length;
    const inProgress = marketingSchedule.filter(item => item.status === 'in_progress').length;
    const planned = marketingSchedule.filter(item => item.status === 'planned').length;
    
    return {
      total,
      completed,
      inProgress,
      planned,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  const stats = getProgressStats();
  const filteredSchedule = getFilteredSchedule();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        
        {/* Header with Stats */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Campaign />
              Marketing Schedule
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setScheduleDialogOpen(true)}
            >
              Add Task
            </Button>
          </Box>

          {/* Progress Overview */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary.main">{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Tasks</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="success.main">{stats.completed}</Typography>
                  <Typography variant="body2" color="text.secondary">Completed</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="warning.main">{stats.inProgress}</Typography>
                  <Typography variant="body2" color="text.secondary">In Progress</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="info.main">{stats.completionRate}%</Typography>
                  <Typography variant="body2" color="text.secondary">Completion</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Progress Bar */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Overall Progress</Typography>
                <Typography variant="body2" color="text.secondary">
                  {stats.completed} of {stats.total} tasks completed
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={stats.completionRate} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </CardContent>
          </Card>
        </Box>

        {/* Filter Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons="auto"
          >
            <Tab label={`All (${getTabCount('all')})`} />
            <Tab label={`In Progress (${getTabCount('in_progress')})`} />
            <Tab label={`Completed (${getTabCount('completed')})`} />
            <Tab label={`Other (${getTabCount('other')})`} />
          </Tabs>
        </Card>

        {/* Schedule List */}
        <Card>
          <CardContent>
            {filteredSchedule.length > 0 ? (
              <List>
                {filteredSchedule
                  .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
                  .map((item, index) => {
                    const actualIndex = marketingSchedule.findIndex(scheduleItem => scheduleItem === item);
                    return (
                      <React.Fragment key={actualIndex}>
                        <ListItem sx={{ py: 2 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ 
                              bgcolor: item.status === 'completed' ? 'success.main' : 
                                      item.status === 'in_progress' ? 'warning.main' : 
                                      item.status === 'cancelled' ? 'error.main' : 'grey.400'
                            }}>
                              {getStatusIcon(item.status)}
                            </Avatar>
                          </ListItemAvatar>
                          
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                  {item.action}
                                </Typography>
                                <Chip 
                                  label={item.platform} 
                                  size="small" 
                                  variant="outlined"
                                />
                                <Chip 
                                  label={item.priority} 
                                  size="small" 
                                  color={getPriorityColor(item.priority)}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarToday fontSize="small" color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(item.scheduledDate).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                  {item.assignedTo && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Person fontSize="small" color="action" />
                                      <Typography variant="body2" color="text.secondary">
                                        {item.assignedTo}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Chip 
                                    label={item.status.replace('_', ' ')} 
                                    size="small" 
                                    color={getStatusColor(item.status)}
                                    variant="filled"
                                  />
                                </Box>
                                {item.notes && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {item.notes}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          
                          <ListItemSecondaryAction>
                            <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                              {item.status !== 'completed' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  onClick={() => updateScheduleItemStatus(actualIndex, 'completed')}
                                >
                                  Complete
                                </Button>
                              )}
                              {item.status === 'planned' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => updateScheduleItemStatus(actualIndex, 'in_progress')}
                                >
                                  Start
                                </Button>
                              )}
                              <IconButton 
                                size="small"
                                onClick={() => editScheduleItem(actualIndex)}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton 
                                size="small"
                                color="error"
                                onClick={() => removeScheduleItem(actualIndex)}
                              >
                                <Delete />
                              </IconButton>
                            </Stack>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < filteredSchedule.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
              </List>
            ) : (
              <Alert 
                severity="info" 
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => setScheduleDialogOpen(true)}
                  >
                    Add First Task
                  </Button>
                }
              >
                {activeTab === 0 
                  ? "No marketing tasks scheduled yet. Start by adding your first marketing activity!"
                  : `No ${activeTab === 1 ? 'in progress' : activeTab === 2 ? 'completed' : 'other status'} tasks found.`
                }
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Task Dialog */}
        <Dialog 
          open={scheduleDialogOpen} 
          onClose={resetForm} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {editingIndex !== null ? 'Edit Marketing Task' : 'Add Marketing Task'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={newScheduleItem.action}
                    label="Action Type"
                    onChange={(e) => setNewScheduleItem(prev => ({ ...prev, action: e.target.value }))}
                  >
                    {actionTypes.map(action => (
                      <MenuItem key={action} value={action}>{action}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={newScheduleItem.platform}
                    label="Platform"
                    onChange={(e) => setNewScheduleItem(prev => ({ ...prev, platform: e.target.value }))}
                  >
                    {marketingPlatforms.map(platform => (
                      <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Scheduled Date"
                  value={newScheduleItem.scheduledDate}
                  onChange={(date) => setNewScheduleItem(prev => ({ 
                    ...prev, 
                    scheduledDate: date || new Date() 
                  }))}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={newScheduleItem.priority}
                    label="Priority"
                    onChange={(e) => setNewScheduleItem(prev => ({ 
                      ...prev, 
                      priority: e.target.value as 'low' | 'medium' | 'high' 
                    }))}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newScheduleItem.status}
                    label="Status"
                    onChange={(e) => setNewScheduleItem(prev => ({ 
                      ...prev, 
                      status: e.target.value as MarketingScheduleItem['status']
                    }))}
                  >
                    <MenuItem value="planned">Planned</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="deferred">Deferred</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="skipped">Skipped</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={newScheduleItem.assignedTo || ''}
                    label="Assigned To"
                    onChange={(e) => setNewScheduleItem(prev => ({ 
                      ...prev, 
                      assignedTo: e.target.value 
                    }))}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {teamMembers.map(member => (
                      <MenuItem key={member} value={member}>{member}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={newScheduleItem.notes}
                  onChange={(e) => setNewScheduleItem(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional details, requirements, or notes for this task..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm}>Cancel</Button>
            <Button 
              onClick={addOrUpdateScheduleItem}
              variant="contained"
              disabled={!newScheduleItem.action || !newScheduleItem.platform}
            >
              {editingIndex !== null ? 'Update Task' : 'Add Task'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default MarketingSchedule;